import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { OperationResult, useMutation, useQuery } from "urql";

import { PageHeader } from "@/components/PageHeader";
import { graphql } from "@/gen";
import { ArticleDetailQuery } from "@/gen/graphql";

import ArticleForm, { FormData } from "./components/ArticleForm";
import { ArticleInput } from "./components/articleInput";
import { articleDetailQuery } from "./queries";

const updateRichTextArticleMutation = graphql(`
    mutation UpdateRichTextArticle($id: UUID!, $input: CreateRichTextArticleInput!) {
        updateRichTextArticle(id: $id, input: $input) {
            id
        }
    }
`);

const updateExternalUrlArticleMutation = graphql(`
    mutation UpdateExternalUrlArticle($id: UUID!, $input: CreateExternalUrlArticleInput!) {
        updateExternalUrlArticle(id: $id, input: $input) {
            id
        }
    }
`);

const updateStoryArticleMutation = graphql(`
    mutation UpdateStoryArticle($id: UUID!, $input: CreateStoryArticleInput!) {
        updateStoryArticle(id: $id, input: $input) {
            id
        }
    }
`);

export default function EditArticleRoute() {
    const navigate = useNavigate();
    const id = useParams().id!;

    const [result] = useQuery({
        query: articleDetailQuery,
        variables: {
            id,
        },
    });
    const { data } = result;

    const [_updateRichTextResult, updateRichTextArticle] = useMutation(
        updateRichTextArticleMutation
    );
    const [_updateExternalUrlResult, updateExternalUrlArticle] = useMutation(
        updateExternalUrlArticleMutation
    );
    const [_updateStoryResult, updateStoryArticle] = useMutation(updateStoryArticleMutation);

    const handleSubmit: SubmitHandler<FormData> = useCallback(
        async data => {
            const handleResult = (result: OperationResult) => {
                if (result.error) {
                    for (const error of result.error.graphQLErrors) {
                        toast.error("Error saving article", { description: error.message });
                    }
                } else if (result.data) {
                    toast.success("Article updated");
                    navigate(`/articles/${id}`);
                }
            };

            if (data.kind === "rich_text") {
                const { kind: _kind, ...input } = data;
                try {
                    handleResult(await updateRichTextArticle({ id, input }));
                } catch (e) {
                    toast.error("Request error", { description: String(e) });
                }
            }
            if (data.kind === "external_url") {
                const { kind: _kind, ...input } = data;
                try {
                    handleResult(await updateExternalUrlArticle({ id, input }));
                } catch (e) {
                    toast.error("Request error", { description: String(e) });
                }
            }
            if (data.kind === "story") {
                const { kind: _kind, ...input } = data;

                const newInputWithCorrectRanks = {
                    ...input,
                    items: input.items.map((item, index) => {
                        return {
                            ...item,
                            rank: index,
                        };
                    }),
                };
                try {
                    handleResult(await updateStoryArticle({ id, input: newInputWithCorrectRanks }));
                } catch (e) {
                    toast.error("Request error", { description: String(e) });
                }
            }
        },
        [navigate, id, updateRichTextArticle, updateExternalUrlArticle, updateStoryArticle]
    );

    const defaultValues = data ? flattenArticleContent(data.article) : undefined;

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader title="Edit article" />

            {data ? (
                <ArticleForm defaultValues={defaultValues} onSubmit={handleSubmit} />
            ) : (
                <p>Loading article data</p>
            )}
        </div>
    );
}

type ArticleContent = ArticleDetailQuery["article"];
function flattenArticleContent(content: ArticleContent): ArticleInput {
    switch (content.content.__typename) {
        case "RichTextArticle":
            return {
                ...content,
                kind: "rich_text",
                content: content.content.content as unknown[],
            };
        case "ExternalUrlArticle":
            return {
                ...content,
                kind: "external_url",
                url: content.content.url,
            };
        case "StoryArticle":
            return {
                ...content,
                kind: "story",
                items: content.content.items
                    .sort((a, b) => a.rank - b.rank)
                    .map(item => {
                        if (item.content.__typename === "StoryArticleImage") {
                            return {
                                id: item.id,
                                rank: item.rank,
                                content: {
                                    image: {
                                        id: item.content.id,
                                        duration: item.content.duration ?? undefined,
                                    },
                                },
                            };
                        } else if (item.content.__typename === "StoryArticleVideo") {
                            return {
                                id: item.id,
                                rank: item.rank,
                                content: {
                                    video: {
                                        id: item.content.id,
                                    },
                                },
                            };
                        } else {
                            throw new Error("Unknown content type");
                        }
                    }),
            };
    }
}
